import React, { useMemo } from 'react';

/**
 * FogOverlay - SVG-based fog of war overlay
 * Uses SVG masks to create "holes" in the fog where areas are revealed
 */
export function FogOverlay({ 
  width = 1920, 
  height = 1080, 
  revealedZones = [], 
  enabled = true,
  settings = {
    opacity: 0.85,
    color: '#1a1a2e',
    blur: 10
  },
  className = ''
}) {
  // Generate unique ID for the mask
  const maskId = useMemo(() => `fog-mask-${Math.random().toString(36).substr(2, 9)}`, []);
  
  if (!enabled || revealedZones.length === 0) {
    // If fog is disabled or fully covered, just show the full fog
    if (!enabled) return null;
    
    return (
      <svg
        className={`absolute inset-0 pointer-events-none ${className}`}
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid slice"
      >
        <rect
          x="0"
          y="0"
          width={width}
          height={height}
          fill={settings.color}
          opacity={settings.opacity}
        />
      </svg>
    );
  }

  // Calculate pixel positions from percentages
  const zones = revealedZones.map(zone => ({
    ...zone,
    cx: zone.x * width,
    cy: zone.y * height,
    r: zone.radius * width
  }));

  return (
    <svg
      className={`absolute inset-0 pointer-events-none ${className}`}
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid slice"
      data-testid="fog-overlay"
    >
      <defs>
        {/* Blur filter for soft edges */}
        <filter id={`${maskId}-blur`}>
          <feGaussianBlur in="SourceGraphic" stdDeviation={settings.blur} />
        </filter>
        
        {/* Mask with revealed zones as white circles */}
        <mask id={maskId}>
          {/* Start with white (full fog) */}
          <rect x="0" y="0" width={width} height={height} fill="white" />
          
          {/* Black circles punch "holes" in the fog */}
          {zones.map((zone, index) => (
            <circle
              key={zone.id || index}
              cx={zone.cx}
              cy={zone.cy}
              r={zone.r}
              fill="black"
              filter={`url(#${maskId}-blur)`}
            />
          ))}
        </mask>
      </defs>
      
      {/* Fog rectangle with mask applied */}
      <rect
        x="0"
        y="0"
        width={width}
        height={height}
        fill={settings.color}
        opacity={settings.opacity}
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}

/**
 * FogBrushPreview - Shows where the fog brush will reveal
 */
export function FogBrushPreview({ 
  x, 
  y, 
  radius, 
  containerWidth = 1920,
  containerHeight = 1080,
  visible = false 
}) {
  if (!visible || x === null || y === null) return null;
  
  const cx = x * containerWidth;
  const cy = y * containerHeight;
  const r = radius * containerWidth;
  
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width="100%"
      height="100%"
      viewBox={`0 0 ${containerWidth} ${containerHeight}`}
      preserveAspectRatio="xMidYMid slice"
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="rgba(59, 130, 246, 0.2)"
        stroke="rgba(59, 130, 246, 0.8)"
        strokeWidth="2"
        strokeDasharray="5,5"
      />
    </svg>
  );
}

export default FogOverlay;
